import { useEffect, useState, type ReactNode } from "react";

import { Dialog } from "@/components/ui/Dialog";
import type { FolderItem } from "@/lib/api";
import { api } from "@/lib/api";

export type FolderDialogState =
  | null
  | { mode: "create" }
  | { mode: "rename"; folder: FolderItem }
  | { mode: "delete"; folder: FolderItem };

export type FolderActionDone =
  | { kind: "create"; path: string }
  | { kind: "rename"; oldPath: string; newPath: string }
  | { kind: "delete"; path: string };

function folderParent(fullPath: string): string {
  const index = fullPath.lastIndexOf("/");
  return index === -1 ? "" : fullPath.slice(0, index);
}

function joinFolderPath(parent: string, segment: string): string {
  const trimmed = segment.trim().replace(/^\/+|\/+$/g, "");
  return parent ? `${parent}/${trimmed}` : trimmed;
}

interface FolderActionsDialogProps {
  state: FolderDialogState;
  parentPath: string;
  onClose: () => void;
  onDone: (result: FolderActionDone) => void;
}

export function FolderActionsDialog({
  state,
  parentPath,
  onClose,
  onDone,
}: FolderActionsDialogProps) {
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (!state) return;
    setLocalError(null);
    setBusy(false);
    if (state.mode === "rename") setInput(state.folder.name);
    else setInput("");
  }, [state]);

  const open = state !== null;

  const handleCreate = async () => {
    if (!state || state.mode !== "create") return;
    const segment = input.trim();
    if (!segment) {
      setLocalError("Informe um nome.");
      return;
    }
    const path = joinFolderPath(parentPath, segment);
    setBusy(true);
    try {
      await api.createFolder(path);
      onDone({ kind: "create", path });
    } catch (error) {
      setLocalError(error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  };

  const handleRename = async () => {
    if (!state || state.mode !== "rename") return;
    const segment = input.trim();
    if (!segment) {
      setLocalError("Informe um nome.");
      return;
    }
    const parent = folderParent(state.folder.path);
    const newPath = joinFolderPath(parent, segment);
    if (newPath === state.folder.path) {
      onClose();
      return;
    }
    setBusy(true);
    try {
      await api.renameFolder(state.folder.path, newPath);
      onDone({ kind: "rename", oldPath: state.folder.path, newPath });
    } catch (error) {
      setLocalError(error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!state || state.mode !== "delete") return;
    setBusy(true);
    try {
      await api.deleteFolder(state.folder.path);
      onDone({ kind: "delete", path: state.folder.path });
    } catch (error) {
      setLocalError(error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  };

  let title = "";
  let body: ReactNode = null;
  let footer: ReactNode = null;

  if (state?.mode === "create") {
    title = "Nova pasta";
    body = (
      <div className="space-y-3">
        <label className="block text-xs uppercase tracking-wide text-ink-400" htmlFor="folder-name-input">
          Nome da pasta
        </label>
        <input
          id="folder-name-input"
          type="text"
          placeholder="Nome da pasta"
          value={input}
          onChange={(event) => setInput(event.target.value)}
          className="w-full rounded border border-ink-600 bg-white px-3 py-2 text-ink-900 placeholder:text-ink-300 outline-none focus:border-emerald-600"
          disabled={busy}
        />
      </div>
    );
    footer = (
      <>
        <button
          type="button"
          className="rounded border border-ink-600 px-4 py-2 text-sm text-ink-200 hover:bg-ink-800"
          onClick={onClose}
          disabled={busy}
        >
          Cancelar
        </button>
        <button
          type="button"
          className="rounded bg-emerald-700 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-600 disabled:opacity-50"
          onClick={() => void handleCreate()}
          disabled={busy}
        >
          Criar
        </button>
      </>
    );
  } else if (state?.mode === "rename") {
    title = "Renomear pasta";
    body = (
      <div className="space-y-3">
        <label className="block text-xs uppercase tracking-wide text-ink-400" htmlFor="folder-rename-input">
          Novo nome
        </label>
        <input
          id="folder-rename-input"
          type="text"
          value={input}
          onChange={(event) => setInput(event.target.value)}
          className="w-full rounded border border-ink-600 bg-white px-3 py-2 text-ink-900 placeholder:text-ink-300 outline-none focus:border-emerald-600"
          disabled={busy}
        />
      </div>
    );
    footer = (
      <>
        <button
          type="button"
          className="rounded border border-ink-600 px-4 py-2 text-sm text-ink-200 hover:bg-ink-800"
          onClick={onClose}
          disabled={busy}
        >
          Cancelar
        </button>
        <button
          type="button"
          className="rounded bg-emerald-700 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-600 disabled:opacity-50"
          onClick={() => void handleRename()}
          disabled={busy}
        >
          Salvar
        </button>
      </>
    );
  } else if (state?.mode === "delete") {
    title = "Excluir pasta";
    body = (
      <p className="leading-relaxed">
        Esta ação remove todos os arquivos dentro de &quot;{state.folder.name}&quot;.
      </p>
    );
    footer = (
      <>
        <button
          type="button"
          className="rounded border border-ink-600 px-4 py-2 text-sm text-ink-200 hover:bg-ink-800"
          onClick={onClose}
          disabled={busy}
        >
          Cancelar
        </button>
        <button
          type="button"
          className="rounded bg-red-800 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
          onClick={() => void handleDelete()}
          disabled={busy}
        >
          Excluir
        </button>
      </>
    );
  }

  return (
    <Dialog title={title} open={open} onClose={busy ? () => {} : onClose} footer={footer}>
      {body}
      {localError ? (
        <p className="mt-3 rounded border border-red-900 bg-red-950/40 px-2 py-1.5 text-xs text-red-300">
          {localError}
        </p>
      ) : null}
    </Dialog>
  );
}
